// ─────────────────────────────────────────────────────────────────────
// Access Management ▸ Effective Access Preview tab — Round 7 (2026).
//
// Read-only "what can this employee actually access, and why" view. Combines
// two existing endpoints (no new backend):
//   GET /api/Permissions/effective/:empId        → full bundle (flags + modules)
//   GET /api/admin/user-permissions/:empId        → permissions tagged by source
//
// Shows, for a chosen employee:
//   • identity + role
//   • every permission with a SOURCE badge (Role / Direct / Department)
//   • the resolved MODULES the user will see (the `true` entries in the
//     bundle's modules map) — i.e. exactly what appears in their sidebar/menu
//   • the key computed access flags (IsItAdmin, IsManager, IsHr, …)
//
// This is the "explainable access" surface: it makes role + direct + department
// + system-rule contributions visible in one place. Purely informational —
// no mutations here.
// ─────────────────────────────────────────────────────────────────────
import React, { useEffect, useMemo, useState } from "react";
import {
    Row, Col, Card, CardHeader, CardBody, Input, Badge, Spinner, Table,
} from "reactstrap";
import {
    fetchPreviewEmployees, fetchEffectiveAccess,
} from "../../../api/accessManagement";

const SOURCE_COLORS = { Direct: "primary", Role: "secondary", Department: "info", Other: "secondary" };

// The boolean flags worth surfacing, with friendly labels. Only flags that are
// TRUE are shown as chips; this list controls label + order.
const FLAG_LABELS = [
    ["isItAdmin", "IT Admin"],
    ["isManager", "Manager"],
    ["isActualManager", "Actual Manager (has reports)"],
    ["isPresident", "President"],
    ["isHr", "HR"],
    ["isFinance", "Finance"],
    ["isDmsManager", "DMS Manager"],
    ["isSuperUser", "Super User"],
    ["isBabu", "Accounting EPO Viewer"],
    ["isMpc", "MPC"],
    ["isShipper", "Shipper"],
    ["isTaskAuditor", "Task Auditor"],
    ["isBetaTester", "Beta Tester"],
    ["isSnSignatory", "SN Signatory"],
    ["isProjectApprovalCommittee", "Project Approval Committee"],
    ["isFlOperations", "FL Operations"],
    ["canViewIndia", "Can View India"],
    ["canViewUsa", "Can View USA"],
];

export default function EffectiveAccessPreviewTab() {
    const [employees, setEmployees] = useState([]);
    const [bootLoading, setBootLoading] = useState(true);
    const [bootError, setBootError]     = useState(null);

    const [search, setSearch]               = useState("");
    const [selectedEmpId, setSelectedEmpId] = useState(null);

    const [bundle, setBundle]     = useState(null);   // EffectiveAccessBundle
    const [snapshot, setSnapshot] = useState(null);   // source-tagged permissions
    const [loading, setLoading]   = useState(false);
    const [error, setError]       = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setBootLoading(true); setBootError(null);
            try {
                const emps = await fetchPreviewEmployees();
                if (!cancelled) setEmployees(emps);
            } catch {
                if (!cancelled) setBootError("Could not load the employee list.");
            } finally {
                if (!cancelled) setBootLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        if (!selectedEmpId) { setBundle(null); setSnapshot(null); return; }
        let cancelled = false;
        (async () => {
            setLoading(true); setError(null);
            try {
                // Round 1: ONE call, ONE gate ("Admin - Access Management").
                // The bundle now carries permissionSources (Direct / Role /
                // Department / Denied) computed by the same resolver login uses.
                const b = await fetchEffectiveAccess(selectedEmpId);
                if (cancelled) return;
                if (!b) { setError("Could not compute effective access for this employee."); setBundle(null); setSnapshot(null); }
                else {
                    setBundle(b);
                    setSnapshot(b.permissionSources ? { permissions: b.permissionSources } : null);
                }
            } catch {
                if (!cancelled) setError("Could not load effective access.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [selectedEmpId]);

    const filteredEmployees = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return employees;
        return employees.filter((e) =>
            (e.empId || "").toLowerCase().includes(q) ||
            (e.name || "").toLowerCase().includes(q) ||
            (e.department || "").toLowerCase().includes(q) ||
            (e.role || "").toLowerCase().includes(q)
        );
    }, [employees, search]);

    // Source lookup by permission name, from the snapshot.
    const sourceByPerm = useMemo(() => {
        const m = new Map();
        for (const p of (snapshot?.permissions || [])) {
            m.set(p.permissionName.toLowerCase(), { source: p.source, description: p.description });
        }
        return m;
    }, [snapshot]);

    // Permissions to display: prefer the source-tagged snapshot list (richer);
    // fall back to the bundle's flat permission list if the snapshot is absent.
    const permRows = useMemo(() => {
        if (snapshot?.permissions?.length) {
            return [...snapshot.permissions].sort((a, b) => a.permissionName.localeCompare(b.permissionName));
        }
        return (bundle?.permissions || []).map((name) => ({
            permissionName: name,
            description: sourceByPerm.get(name.toLowerCase())?.description || "",
            source: sourceByPerm.get(name.toLowerCase())?.source || "Other",
        }));
    }, [snapshot, bundle, sourceByPerm]);

    // Modules the user WILL see = the `true` entries in the bundle's modules map.
    const visibleModules = useMemo(() => {
        const mods = bundle?.modules || {};
        return Object.keys(mods).filter((k) => mods[k]).sort();
    }, [bundle]);
    // Round 7: modules the user will NOT see, with the server's reason.
    const hiddenModules = useMemo(() => {
        const mods = bundle?.modules || {};
        return Object.keys(mods).filter((k) => !mods[k]).sort();
    }, [bundle]);
    const [showHidden, setShowHidden] = useState(false);
    const reasonOf = (k) => bundle?.moduleReasons?.[k] || "";

    const activeFlags = useMemo(() => {
        if (!bundle) return [];
        return FLAG_LABELS.filter(([key]) => !!bundle[key]);
    }, [bundle]);

    if (bootLoading) {
        return <div className="text-center text-muted py-5"><Spinner size="sm" /> Loading…</div>;
    }
    if (bootError) {
        return <div className="alert alert-danger">{bootError}</div>;
    }

    return (
        <Row className="g-3">
            {/* LEFT: employee picker */}
            <Col lg={4}>
                <Card className="border-0 shadow-sm h-100">
                    <CardHeader className="bg-white border-0 pt-3 px-3 pb-2">
                        <h6 className="fw-semibold mb-2">Employees</h6>
                        <Input
                            type="search"
                            bsSize="sm"
                            placeholder="Search by name, empId, dept, role…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </CardHeader>
                    <CardBody className="p-2" style={{ maxHeight: "calc(100vh - 380px)", overflowY: "auto" }}>
                        {filteredEmployees.length === 0 ? (
                            <div className="text-muted small text-center py-4">No matches.</div>
                        ) : (
                            <ul className="list-unstyled mb-0">
                                {filteredEmployees.map((e) => (
                                    <li key={e.empId}>
                                        <button
                                            type="button"
                                            onClick={() => setSelectedEmpId(e.empId)}
                                            className="w-100 text-start border-0 rounded p-2 mb-1"
                                            style={{
                                                background: e.empId === selectedEmpId ? "rgba(91, 80, 214, 0.10)" : "transparent",
                                                color:      e.empId === selectedEmpId ? "#2c2076" : "#212529",
                                                cursor: "pointer",
                                            }}
                                        >
                                            <div className="fw-medium" style={{ fontSize: 13 }}>{e.name || e.empId}</div>
                                            <div className="text-muted" style={{ fontSize: 11 }}>
                                                {e.empId}
                                                {e.role ? ` · ${e.role}` : ""}
                                                {e.department ? ` · ${e.department}` : ""}
                                            </div>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </CardBody>
                </Card>
            </Col>

            {/* RIGHT: effective access */}
            <Col lg={8}>
                <Card className="border-0 shadow-sm h-100">
                    {!selectedEmpId ? (
                        <CardBody className="text-center text-muted py-5">
                            <i className="ri-eye-line" style={{ fontSize: 32, opacity: 0.4 }} />
                            <div className="mt-2">Pick an employee to preview their access.</div>
                        </CardBody>
                    ) : loading ? (
                        <CardBody className="text-center text-muted py-5"><Spinner size="sm" /> Computing…</CardBody>
                    ) : error ? (
                        <CardBody><div className="alert alert-danger mb-0">{error}</div></CardBody>
                    ) : bundle ? (
                        <CardBody className="p-3">
                            {/* Identity */}
                            <div className="mb-3">
                                <h6 className="fw-semibold mb-1">{bundle.name || bundle.empId}</h6>
                                <span className="text-muted small">
                                    {bundle.empId}
                                    {bundle.role ? ` · ${bundle.role}` : " · (no role)"}
                                    {bundle.department ? ` · ${bundle.department}` : ""}
                                    {bundle.country ? ` · ${bundle.country}` : ""}
                                </span>
                                {/* Round 1: status + login decision (only Active may authenticate). */}
                                <div className="mt-1">
                                    <span className={`badge ${bundle.canLogin ? "bg-success-subtle text-success" : "bg-danger-subtle text-danger"}`}>
                                        <i className={`${bundle.canLogin ? "ri-checkbox-circle-line" : "ri-close-circle-line"} me-1`} />
                                        {bundle.canLogin ? "Can log in" : "Cannot log in"}
                                        {bundle.empStatus ? ` · status ${bundle.empStatus}` : " · status unknown"}
                                    </span>
                                    {!bundle.canLogin && (
                                        <span className="text-muted small ms-2">Only employees with status A (Active) can authenticate.</span>
                                    )}
                                </div>
                            </div>

                            {/* Access flags */}
                            <div className="mb-3">
                                <div className="text-uppercase text-muted mb-2" style={{ fontSize: 11, letterSpacing: 0.4 }}>
                                    Access flags
                                </div>
                                {activeFlags.length === 0 ? (
                                    <span className="text-muted small">No special flags — standard employee access.</span>
                                ) : (
                                    <div className="d-flex flex-wrap gap-1">
                                        {activeFlags.map(([key, label]) => (
                                            <Badge key={key} color="dark" className="bg-dark-subtle text-dark" style={{ fontWeight: 500 }}>
                                                {label}
                                            </Badge>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Visible modules */}
                            <div className="mb-3">
                                <div className="text-uppercase text-muted mb-2" style={{ fontSize: 11, letterSpacing: 0.4 }}>
                                    Modules this user will see ({visibleModules.length})
                                </div>
                                {visibleModules.length === 0 ? (
                                    <span className="text-muted small">No modules resolved.</span>
                                ) : (
                                    <div className="d-flex flex-wrap gap-1">
                                        {visibleModules.map((m) => (
                                            <Badge key={m} color="success" className="bg-success-subtle text-success"
                                                   style={{ fontWeight: 500, fontFamily: "monospace" }}
                                                   title={reasonOf(m)}>
                                                {m}
                                            </Badge>
                                        ))}
                                    </div>
                                )}
                                {/* Round 7: why — hover any badge; expand for the hidden ones */}
                                <div className="mt-2">
                                    <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setShowHidden((v) => !v)}>
                                        {showHidden ? "Hide" : "Show"} the {hiddenModules.length} module{hiddenModules.length === 1 ? "" : "s"} this user will NOT see (with reasons)
                                    </button>
                                    {showHidden && (
                                        <table className="table table-sm mb-0 mt-2" style={{ fontSize: 12 }}>
                                            <tbody>
                                                {hiddenModules.map((m) => (
                                                    <tr key={m}>
                                                        <td style={{ fontFamily: "monospace", whiteSpace: "nowrap" }}>{m}</td>
                                                        <td className="text-muted">{reasonOf(m)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                                {bundle?.lastLoginUtc && (
                                    <div className="small text-muted mt-2">
                                        Last successful login: {new Date(bundle.lastLoginUtc).toLocaleString()} (from the audit trail)
                                    </div>
                                )}
                                {bundle?.enforcementSwitches && Object.values(bundle.enforcementSwitches).some((v) => v === false) && (
                                    <div className="small text-muted mt-1">
                                        Backend enforcement switched OFF for:{" "}
                                        {Object.entries(bundle.enforcementSwitches).filter(([, v]) => !v).map(([k]) => k).join(", ")}
                                        {" "}— those modules' APIs accept any signed-in employee until the switch is enabled.
                                    </div>
                                )}
                            </div>

                            {/* Permissions with source */}
                            <div>
                                <div className="text-uppercase text-muted mb-2" style={{ fontSize: 11, letterSpacing: 0.4 }}>
                                    Permissions ({permRows.length}) &amp; source
                                </div>
                                {permRows.length === 0 ? (
                                    <span className="text-muted small">No permissions.</span>
                                ) : (
                                    <Table responsive borderless className="mb-0 align-middle">
                                        <thead>
                                            <tr style={{ fontSize: 12, color: "#8a93a8", textTransform: "uppercase", letterSpacing: 0.4 }}>
                                                <th style={{ width: "45%" }}>Permission</th>
                                                <th style={{ width: "40%" }}>Description</th>
                                                <th style={{ width: "15%" }}>Source</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {permRows.map((p) => (
                                                <tr key={p.permissionName} style={{ borderTop: "1px solid #eef0f4" }}>
                                                    <td className="fw-medium">{p.permissionName}</td>
                                                    <td className="text-muted small">{p.description || "—"}</td>
                                                    <td>
                                                        <Badge color={SOURCE_COLORS[p.source] || "secondary"} pill style={{ fontWeight: 500 }}>
                                                            {p.source || "Other"}
                                                        </Badge>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                )}
                            </div>
                        </CardBody>
                    ) : null}
                </Card>
            </Col>
        </Row>
    );
}
